import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileSpreadsheet, Calendar, FileText, Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCurrentStudy } from "@/context/CurrentStudyContext";

const FORMAT_LABEL = {
  classic: { label: "Clásico (CN + Unidades)", color: "bg-slate-100 text-slate-700" },
  matrix: { label: "Matriz por empleado", color: "bg-emerald-100 text-emerald-700" },
  diary: { label: "Diario de ventas", color: "bg-sky-100 text-sky-700" },
};

export default function UploadPage() {
  const [file, setFile] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [months, setMonths] = useState(1);
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);  // {format, min_date, max_date, employees_detected, ...}
  const [previewing, setPreviewing] = useState(false);
  const navigate = useNavigate();
  const { setCurrent } = useCurrentStudy();

  const runPreview = async (f) => {
    setFile(f);
    setPreview(null);
    if (!f) return;
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/dispensations/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // Auto-rellenar fechas si las detectó
      if (data.min_date) setStart(data.min_date);
      if (data.max_date) setEnd(data.max_date);
      // Calcular meses entre fechas
      if (data.min_date && data.max_date) {
        const a = new Date(data.min_date);
        const b = new Date(data.max_date);
        const diffMonths = Math.max(1, Math.round((b - a) / (1000 * 60 * 60 * 24 * 30)));
        setMonths(diffMonths);
      }
      if (!label && data.min_date) {
        const d = new Date(data.min_date);
        const monthName = d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
        setLabel(`Estudio ${monthName}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "No se pudo pre-analizar el archivo";
      toast.error(typeof detail === "string" ? detail : "Error al pre-analizar");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !start || !end) {
      toast.error("Selecciona archivo y periodo");
      return;
    }
    if (new Date(start) > new Date(end)) {
      toast.error("La fecha de inicio debe ser anterior a la de fin");
      return;
    }
    if (!months || months < 1) {
      toast.error("Indica cuántos meses cubre el fichero");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("period_start", start);
    fd.append("period_end", end);
    fd.append("months", String(months));
    fd.append("label", label);
    setSubmitting(true);
    try {
      const { data } = await api.post("/dispensations/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Cargadas ${data.total_lines} líneas (${data.total_units} unidades)`);
      const discarded = (data.discarded_no_gh || 0) + (data.discarded_only_marca || 0);
      if (discarded > 0) {
        toast.info(
          `Se descartaron ${discarded} líneas sin alternativa genérica (${data.discarded_no_gh} sin GH, ${data.discarded_only_marca} GH 100% marca, ${data.discarded_units || 0} uds).`,
          { duration: 8000 }
        );
      }
      // Si es de un mes anterior al más reciente, el vigente sigue siendo el más reciente.
      const latest = data.latest_study;
      if (!data.is_latest_period && latest) {
        await setCurrent(latest.id);
        const latestName = latest.label || latest.period_end;
        toast.warning(`Este estudio es anterior a «${latestName}»`, {
          description: `«${data.label || "El estudio subido"}» se ha guardado, pero el estudio vigente sigue siendo «${latestName}», que es el más reciente.`,
          duration: 15000,
          action: { label: "Usar este como vigente", onClick: () => setCurrent(data.id) },
        });
      } else {
        await setCurrent(data.id);
      }
      navigate(`/resumen/${data.id}`, { state: { remindStock: !!data.is_latest_period } });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error al subir";
      toast.error(typeof detail === "string" ? detail : "Error al subir");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-8 lg:p-12" data-testid="upload-page">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-10 items-start max-w-7xl">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
            Nueva carga
          </div>
      <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950 mb-2">
        Subir dispensación
      </h1>
      <p className="text-slate-500 mb-6">
        El archivo debe contener al menos dos columnas: <strong>Código Nacional</strong> y{" "}
        <strong>Unidades</strong>. Sólo se procesan CN superiores a 600.000.
      </p>
      <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-10 text-sm" data-testid="upload-hint-employee">
        <div className="font-bold text-sky-900 mb-1 flex items-center gap-2">
          <Sparkles className="h-4 w-4" /> Tres formatos válidos (detección automática)
        </div>
        <ol className="list-decimal list-inside text-xs text-sky-900/80 space-y-1 mt-2">
          <li><strong>Clásico</strong> — 2 columnas: <span className="font-mono">CN | Unidades</span></li>
          <li><strong>Matriz por empleado</strong> — cabecera: <span className="font-mono">CN | (1)1 NOMBRE | (2)2 NOMBRE | …</span></li>
          <li><strong>Diario de Ventas</strong> — exportación con columnas <span className="font-mono">Fecha | Hora | Vendedor | Código | Uni. | …</span></li>
        </ol>
        <p className="text-xs text-sky-900/70 mt-2">
          En los formatos con empleados, los nombres se crearán en <strong>Personal</strong> pendientes de revisar.
          El sistema detecta y pre-rellena las fechas si están en el archivo.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8" data-testid="upload-form">
        {/* Period */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Inicio del periodo" icon={Calendar}>
            <input
              data-testid="period-start-input"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
              required
            />
          </Field>
          <Field label="Fin del periodo" icon={Calendar}>
            <input
              data-testid="period-end-input"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
              required
            />
          </Field>
        </div>

        <Field label="Etiqueta (opcional)" icon={FileText}>
          <input
            data-testid="label-input"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ej: Dispensación enero 2026"
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </Field>

        {/* File dropzone */}
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
            Archivo
          </div>
          <label
            data-testid="upload-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files?.[0]) runPreview(e.dataTransfer.files[0]);
            }}
            className={`block border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-all ${
              dragOver
                ? "border-slate-900 bg-slate-50"
                : "border-slate-300 bg-white hover:border-slate-500 hover:bg-slate-50"
            }`}
          >
            <input
              type="file"
              accept=".xlsx,.xls,.csv,.txt,.pdf"
              className="hidden"
              onChange={(e) => runPreview(e.target.files?.[0] || null)}
              data-testid="file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileSpreadsheet className="h-8 w-8 text-slate-700" strokeWidth={1.5} />
                <div>
                  <div className="font-medium text-slate-900">{file.name}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {(file.size / 1024).toFixed(1)} KB · Click para cambiar
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Upload className="h-8 w-8 mx-auto text-slate-400" strokeWidth={1.5} />
                <div>
                  <div className="font-medium text-slate-900">Arrastra el archivo o haz click</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Formatos soportados: .xlsx · .xls · .csv · .txt · .pdf (listado de líneas de venta IOFWIN)
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    Programas reconocidos: Unycop · Unycop Next · Farmatic · Nixfarma · Bitfarma · IOFwin · Diario clásico CN/uds
                  </div>
                </div>
              </div>
            )}
          </label>

          {/* Banner de pre-análisis */}
          {previewing && (
            <div className="mt-4 flex items-center gap-2 text-sm text-slate-500" data-testid="preview-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Analizando el archivo…
            </div>
          )}
          {preview && !previewing && (
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="preview-banner">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <strong className="text-emerald-900">Archivo analizado correctamente</strong>
                    {FORMAT_LABEL[preview.format] && (
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${FORMAT_LABEL[preview.format].color}`}>
                        {FORMAT_LABEL[preview.format].label}
                      </span>
                    )}
                  </div>
                  <ul className="text-sm text-slate-700 space-y-0.5">
                    <li>📊 <strong>{preview.lines_total}</strong> líneas · <strong>{preview.total_units}</strong> unidades totales</li>
                    {preview.min_date && preview.max_date && (
                      <li>📅 Periodo detectado: <strong>{preview.min_date}</strong> a <strong>{preview.max_date}</strong> <span className="text-slate-500 text-xs">(pre-rellenado abajo, modifícalo si no es correcto)</span></li>
                    )}
                    {preview.has_employee_breakdown && (
                      <li>👥 <strong>{preview.employees_detected.length}</strong> empleados detectados: <span className="text-xs">{preview.employees_detected.join(" · ")}</span></li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          data-testid="upload-submit"
          type="submit"
          disabled={submitting}
          className="bg-slate-950 text-white px-6 py-3 rounded-md font-medium hover:bg-slate-800 transition-all disabled:opacity-60"
        >
          {submitting ? "Procesando…" : "Procesar archivo"}
        </button>
      </form>
        </div>

        {/* Sidebar visual · Beneficios ocultos */}
        <aside className="hidden lg:block lg:sticky lg:top-8" data-testid="upload-sidebar">
          <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-slate-950">
            <img
              src="/img/farmaclass-upload.jpg"
              alt="FarmaClass · Descubre los beneficios ocultos de tu farmacia"
              className="w-full h-auto object-cover block"
              loading="lazy"
            />
          </div>
          <div className="mt-4 px-1 text-[11px] text-slate-500 leading-relaxed">
            Cada estudio que subes detecta oportunidades de conversión <strong className="text-slate-700">invisibles</strong> en tu mezcla marca/genérico — sin perder ni un cliente.
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2 block">
        {label}
      </label>
      {children}
    </div>
  );
}

function monthsBetween(startStr, endStr) {
  const s = new Date(startStr);
  const e = new Date(endStr);
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  const diff =
    (e.getFullYear() - s.getFullYear()) * 12 +
    (e.getMonth() - s.getMonth()) +
    (e.getDate() >= s.getDate() ? 1 : 0);
  return Math.max(1, Math.min(60, diff));
}
